/**
 * Alta —y edición— de una oferta, con el mismo asistente que el de servicios
 * reservables.
 *
 * ── Uno para las dos cosas ───────────────────────────────────────────────────
 * Con `offer` puesto, edita esa oferta; sin él, crea una. No son dos pantallas
 * parecidas a propósito: los campos, el orden, las validaciones y la vista
 * previa son idénticos, así que duplicarlos garantizaba que un día divergieran
 * —que el alta pidiera algo que la edición no deja cambiar, o al revés—.
 *
 * Es lo mismo que hizo la ficha de un servicio reservable, y por el mismo
 * motivo: allí había cuatro pestañas que repetían campo por campo lo que
 * pregunta el asistente. Aquí eran cinco. La ficha se quedó con lo que el
 * asistente no toca y cada dato de su resumen lleva su atajo al paso donde se
 * cambia — ver `OfferDetail`.
 *
 * ── Por qué comparte el armazón ──────────────────────────────────────────────
 * Antes era un cuadro pequeño con tres pasos y una tarjeta de muestra al lado.
 * Funcionaba, pero era OTRA cosa: quien acababa de crear un servicio reservable
 * —pantalla completa, raíl de iconos, la página de verdad maquetada a la
 * derecha— se encontraba aquí con un formulario que no se le parecía en nada,
 * para una decisión que es la misma: qué vendo, cuánto cuesta, qué pasa después.
 *
 * Así que se reutilizan las clases `dash-svc-wizard-*`. NO son del asistente de
 * servicios: son EL asistente del panel, y esa es la única forma de que los dos
 * sigan pareciéndose dentro de un año. Copiar el CSS con otro prefijo habría
 * dejado dos armazones que se separan al primer retoque.
 *
 * ── Qué pregunta, y por qué eso ──────────────────────────────────────────────
 * Cuatro pasos, que son las cuatro decisiones que hay que tomar antes de que
 * alguien pueda pagar:
 *
 *   1. Qué vendes      — nombre, descripción, tipo y portada.
 *   2. Cuánto cuesta   — pago único o suscripción, importe, y quién se lleva qué.
 *   3. Qué pasa después — a dónde va el comprador cuando termina de pagar.
 *   4. Revisión        — el resumen, y si nace en borrador o a la venta.
 *
 * El tercero es el que no existía. Sin él, toda oferta nacía con la página de
 * gracias incluida y casi nadie descubría que se podía cambiar: estaba a dos
 * pantallas de aquí, en la ficha, dentro de una sección que hay que ir a buscar.
 *
 * ── La vista previa ──────────────────────────────────────────────────────────
 * La página de pago de Stripe, que es literalmente lo que ve el comprador: el
 * cobro no ocurre en el sitio, se sale a `checkout.stripe.com`. Ahí es donde el
 * título, la descripción, la portada y el importe aparecen juntos por primera
 * vez, así que es lo único que responde «¿esto qué aspecto tiene?».
 *
 * En el paso de «después de pagar» la maqueta se aparta y deja ver el destino
 * elegido, por lo mismo que el asistente de servicios cambia la página por el
 * probador en el paso del horario: lo que se está decidiendo ahí no se ve en la
 * página de pago.
 */
import { CurrencySymbol } from './CurrencySymbol'
import { OfferBrandField, OfferTagsField } from './OfferBrandField'
import type { KnownOption } from './BlogPickerField'
import { createElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { offerShortDescription } from '@webbuilder/schemas'
import { OfferDescriptionFields } from './OfferDescriptionFields'
import { createPortal } from 'react-dom'
import {
  IconArrowLeft,
  IconArrowRight,
  IconAt,
  IconCheck,
  IconCoin,
  IconCreditCard,
  IconExternalLink,
  IconGift,
  IconEye,
  IconListDetails,
  IconFileTypePdf,
  IconInfoCircle,
  IconLoader2,
  IconMapPin,
  IconPackage,
  IconPencil,
  IconRepeat,
  IconTrash,
  IconUpload,
  IconUser,
  IconWorld,
  IconTruckDelivery,
  IconInfinity,
  IconStack2,
} from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { Notice } from '../Notice'
import { PAYMENTS_UPSELL, UpsellCard } from '../FeatureUpsell'
import { StripeMark } from './StripeMark'
import { StripeOnboardingDialog } from './StripeOnboardingDialog'
import { PublishBlockDialog } from './OfferStatusSelect'
import { OfferGalleryField, PreviewGallery } from './OfferGalleryField'
import { OfferCustomFieldsEditor, offerTemplateKindOf } from './OfferCustomFields'
import { addOfferPrice, createOffer, getShipping, listPages, removeOfferFile, removeOfferPrice, renameOfferFile, saveOfferVariants, updateOffer, uploadOfferFile, type Offer, type OfferFile, type OfferPrice, type PageMeta, type ShippingRate } from '../../../lib/api'
import { CADENCES, cadenceIdOf, cadenceKeyOf, fileIconFor, fileSizeLabel, formatMoney, minorUnitFactor, toMinorUnits, type CadenceId } from './offerShared'
import { useRegion } from '../../../lib/region'
import { useStripeConnect } from '../../../lib/stripeConnect'
import { PlanLimitModal } from '../PlanLimitModal'
import { reachedLimit, SITE_PLAN_LIMITS } from '../../../lib/planLimits'
import type { BillingPlan } from '../../../lib/api'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { WizardTopBar } from '../WizardTopBar'
import { PendingChangesBar } from '../../DashboardTopbarActions'
import { useScrollTopOnStep } from '../../../lib/useScrollTopOnStep'
import { ShippingRatesEditor, rateSummary } from './ShippingRatesEditor'
import { VariantsEditor, combinationsOf, commitPendingVariants, finalizeVariants, variantsChanged, variantsDraftOf, type VariantRow, type VariantsDraft } from './VariantsEditor'

/**
 * Las tres formas de cobrar esto, y una de ellas es no cobrarlo.
 *
 * `free` no es «precio vacío»: vacío significa «aún no lo he decidido» y deja la
 * oferta sin poder venderse. Gratis es una decisión, y se guarda como un precio
 * de importe cero — que es lo que le permite al botón de la página entregar sin
 * pasar por Stripe. Ver `isFree` en el worker.
 */
type Mode = 'free' | 'one_time' | 'subscription'

/* El dominio de la página de pago. Fuera del JSX porque no es texto que se
   traduzca —es una dirección— y dentro lo era para el linter del catálogo. */
const CHECKOUT_HOST = 'checkout.stripe.com'

/** El mismo tope que pone el servidor, para avisar antes de mandar nada. */
const MAX_OFFER_FILE_BYTES = 15 * 1024 * 1024
/** Y los mismos topes de cuántas cosas caben, que también pone el servidor. */
const MAX_OFFER_FILES = 5
/* Una portada y nada más: es la única imagen que sale en la página de pago, en
   la lista y en la cabecera de la oferta. El número vive aquí y no repartido en
   los `slice` de abajo. */

/* `key` y no el texto: la lista se arma al cargar el archivo, cuando todavía no
   hay traductor y el idioma puede cambiar después. Igual que en `svcw`. */
/**
 * Los pasos, y por qué la lista no es fija.
 *
 * Un producto digital tiene un paso que los demás no: el archivo. Y no es un
 * campo más —es literalmente lo que se vende—, así que va en su propio sitio y
 * no colgando del final del paso de la entrega, donde había que bajar hasta el
 * tercer bloque para encontrarlo.
 *
 * Por eso los pasos se identifican por CLAVE y no por posición. Con índices,
 * una lista que cambia de largo convierte cada `setStep(2)` en una apuesta
 * sobre qué había en el dos esta vez.
 */
type StepKey = 'info' | 'files' | 'price' | 'variants' | 'shipping' | 'delivery' | 'extra' | 'review'

/** El mismo tipo, para quien abre el asistente por un paso concreto. */
export type OfferWizardStep = StepKey

const ALL_STEPS: { key: StepKey; labelKey: string; Icon: typeof IconInfoCircle }[] = [
  { key: 'info', labelKey: 'offers.new.step.info', Icon: IconInfoCircle },
  { key: 'files', labelKey: 'offers.new.step.files', Icon: IconFileTypePdf },
  { key: 'price', labelKey: 'offers.new.step.price', Icon: IconCoin },
  /* Sólo en un producto físico: sus tallas, colores… con lo que queda de
     cada combinación; y después cuántos hay y cómo llegan. */
  { key: 'variants', labelKey: 'offers.new.step.variants', Icon: IconStack2 },
  { key: 'shipping', labelKey: 'offers.new.step.shipping', Icon: IconTruckDelivery },
  { key: 'delivery', labelKey: 'offers.new.step.delivery', Icon: IconPackage },
  /* Lo propio de esta oferta que su página enseña: «Ingredientes», «Cómo
     usar»… Ver `OfferCustomFields`. Opcional: se puede pasar sin tocarlo. */
  { key: 'extra', labelKey: 'offers.new.step.extra', Icon: IconListDetails },
  { key: 'review', labelKey: 'offers.new.step.review', Icon: IconEye },
]

/** El «/mes» que va pegado al importe en la maqueta. Por cadencia, no por unidad. */
const CADENCE_SUFFIX_KEY: Record<CadenceId, string> = {
  week: 'offers.new.suffix.week',
  biweek: 'offers.new.suffix.biweek',
  month: 'offers.new.suffix.month',
  quarter: 'offers.new.suffix.quarter',
  half: 'offers.new.suffix.half',
  year: 'offers.new.suffix.year',
}

/**
 * A qué parte de la maqueta mira la cámara en cada paso.
 *
 * La página de pago cabe entera en la columna, pero entera es ilegible: el
 * título mide quince píxeles y quien lo está escribiendo no puede comprobar si
 * cabe. Acercarse a lo que se edita es lo que convierte la vista previa en algo
 * que se mira en vez de un adorno.
 *
 * El tercer paso no tiene encuadre porque ahí no hay maqueta: se cambia por el
 * destino elegido. Y el cuarto mira a la página entera a propósito —revisar es
 * lo contrario de editar—.
 */
type PreviewZone = 'media' | 'title' | 'desc' | 'details' | 'price' | 'summary' | 'shipping' | 'full'
const PREVIEW_FOCUS: Record<StepKey, PreviewZone> = {
  info: 'summary',
  /* El archivo no se ve en la página de pago: la maqueta se queda entera. */
  files: 'full',
  price: 'price',
  /* Las variantes se eligen junto al precio en la página del producto. */
  variants: 'price',
  shipping: 'shipping',
  delivery: 'full',
  extra: 'full',
  review: 'full',
}

/** Cuánto se acerca cada zona. Las pequeñas piden más aumento. */
const ZONE_SCALE: Record<Exclude<PreviewZone, 'full'>, number> = {
  media: 1.3,
  title: 1.45,
  desc: 1.3,
  details: 1.1,
  price: 1.45,
  summary: 1.15,
  shipping: 1.3,
}

/**
 * El importe tal y como se verá, sin esperar al servidor.
 *
 * El servidor devuelve `formatted` con la regla de cada moneda, pero eso llega
 * cuando la oferta ya existe. Aquí hace falta antes, así que se formatea en
 * local con el mismo `formatMoney` que usa el resto del panel — y por eso no se
 * usa para nada que se guarde.
 */
function previewAmount(raw: string, currency: string): string {
  const minor = toMinorUnits(raw, currency)
  if (minor === null || !raw.trim()) return ''
  return formatMoney(minor, currency)
}

export function NewOfferWizard({ projectId, token, canConnectStripe, plan, publishedOffers, brands = [], onBrandRemoved, tags: knownTagList = [], onTagRemoved, onClose, onCreated, onOpenPlans, initialType, offer, initialStep }: {
  projectId: string
  token: string
  /** Si el plan permite conectar Stripe. Ver `assertCanConnectStripe`. */
  canConnectStripe: boolean
  /** El plan del proyecto y cuántas ofertas publicadas tiene ya: con los dos se
   *  sabe, antes de pulsar, si «a la venta» cabe. Ver `PlanLimits.offers`. */
  plan: BillingPlan
  publishedOffers: number
  /** Las marcas que ya usa el proyecto, con cuántas ofertas las llevan: se eligen o se crea otra. */
  brands?: KnownOption[]
  /** Se quitó una marca de todas las ofertas desde la lista. */
  onBrandRemoved?: (name: string) => void
  /** Las etiquetas que ya usa el proyecto, con cuántas ofertas las llevan. */
  tags?: KnownOption[]
  onTagRemoved?: (name: string) => void
  onClose: () => void
  onCreated: (offer: Offer) => void
  onOpenPlans: () => void
  /**
   * Con qué tipo arranca, que ya se eligió en la puerta.
   *
   * La puerta decide por dónde se entra, no encierra: editando, el tipo vuelve
   * a estar en el primer paso. Sin él vuelve a `digital`, que es lo más común y
   * lo único que además pide archivo.
   */
  initialType?: Offer['type'] | undefined
  /** La oferta que se está editando, o ausente para crear una nueva. */
  offer?: Offer | null | undefined
  /** Por qué paso abrir. Sólo al editar: crear empieza por el principio. */
  initialStep?: StepKey | undefined
}) {
  const { t } = useI18n()
  const editing = Boolean(offer)
  /* «Por completar»: la empezó la IA. Se edita, pero como al crear: desde el
     principio y paso a paso, sin saltar a ninguno —lo que sólo sabe el
     cliente (el archivo, su precio) no se puede dar por bueno—. */
  const completing = Boolean(offer?.setupPending)
  const [step, setStep] = useState<StepKey>(completing ? 'info' : initialStep ?? 'info')

  /* Abrir los planes en una ventana nueva para no perder el progreso del asistente:
     si se navegase dentro de la misma pantalla, el asistente se desmontaría y con
     él todos los campos, textos y archivos que se acaban de rellenar. */
  const handleOpenPlans = useCallback(() => {
    const url = `${window.location.pathname}?project=${encodeURIComponent(projectId)}&view=plans`
    const opened = window.open(url, '_blank')
    if (!opened && onOpenPlans) {
      onOpenPlans()
    }
  }, [projectId, onOpenPlans])

  // ── Paso 1: qué vendes ─────────────────────────────────────────────────────
  const [title, setTitle] = useState(offer?.title ?? '')
  const [brand, setBrand] = useState(offer?.brand ?? '')
  const [tags, setTags] = useState<string[]>(offer?.tags ?? [])
  const [description, setDescription] = useState(offer?.description ?? '')
  const [shortDescription, setShortDescription] = useState(offer ? offerShortDescription(offer) : '')
  /*
   * El tipo no se elige aquí, ni creando ni editando.
   *
   * Creando lo decidió la puerta, y volver a preguntarlo era hacer contestar dos
   * veces lo mismo en el mismo gesto — con la respuesta ya puesta, además, así
   * que parecía que se podía haber abierto el formulario equivocado.
   *
   * Y editando tampoco, que es lo que se quitó después: un producto digital y un
   * servicio no son dos etiquetas de la misma cosa. Lo digital se entrega con
   * sus archivos —que ya están subidos y colgando de esa oferta— y un servicio
   * no entrega nada; convertir uno en otro deja los archivos huérfanos de una
   * oferta que no los descarga y cambia lo que ya compró alguien. Si de verdad
   * es otra cosa, es otra oferta.
   *
   * De paso los pasos dejan de aparecer y desaparecer a mitad del recorrido: el
   * del archivo depende del tipo, y el tipo no se mueve.
   */
  const type: Offer['type'] = offer?.type ?? initialType ?? 'digital'
  /*
   * La galería entera, no sólo la portada.
   *
   * La primera es la portada —es la que sale en la página de pago y en la
   * lista—, y las demás acompañan. Vivían en la ficha, en su propia pestaña;
   * traerlas aquí es lo que permite que allí no quede un segundo sitio donde
   * cambiar las imágenes de la misma oferta.
   */
  const [images, setImages] = useState<string[]>(offer?.images ?? [])
  /* Los campos personalizados de su tipo, por id. Ver `OfferCustomFields`. */
  const [customValues, setCustomValues] = useState<Record<string, string>>(offer?.customValues ?? {})
  const image = images[0] ?? ''

  // ── Paso 2: cuánto cuesta ──────────────────────────────────────────────────
  /* La moneda NO se elige aquí: es la del proyecto entera, y sale de
     Configuración → Región y moneda. Antes había un desplegable en este mismo
     formulario, y el resultado era un catálogo con una oferta en pesos y otra
     en dólares sin que nadie lo hubiera decidido. */
  const { currency } = useRegion()
  /*
   * El precio que manda, leído de vuelta.
   *
   * Los precios de Stripe son inmutables: lo que aquí se llama «cambiar el
   * importe» es dar de alta otro y retirar éste, y por eso el que se enseña es
   * el recomendado —el que usa el botón de compra—. Los demás activos, si los
   * hay, se listan aparte en su paso.
   */
  const storedPrices = (offer?.prices ?? []).filter((row) => row.active)
  const storedPrice = storedPrices.find((row) => row.isDefault) ?? storedPrices[0] ?? null
  const [mode, setMode] = useState<Mode>(() => {
    /* Un producto físico se cobra una vez y nunca gratis: no hay suscripción
       con envío en Stripe, y un pago de cero no pasaría por la caja que pide
       la dirección. */
    if (!storedPrice || (offer?.type ?? initialType) === 'physical') return 'one_time'
    /* Cero es gratis y no «sin precio»: ver `isFree`. */
    if (storedPrice.unitAmount === 0) return 'free'
    return storedPrice.interval === 'one_time' ? 'one_time' : 'subscription'
  })
  const [amount, setAmount] = useState(() => {
    if (!storedPrice || storedPrice.unitAmount === 0) return ''
    const factor = minorUnitFactor(storedPrice.currency)
    /* Con los decimales de SU moneda: en yenes «1000» y no «1000.00», que al
       volver a guardarse se leería como diez yenes. */
    return (storedPrice.unitAmount / factor).toFixed(factor === 1 ? 0 : 2)
  })
  const [cadence, setCadence] = useState<CadenceId>(() => (storedPrice ? cadenceIdOf(storedPrice) : null) ?? 'month')
  /* El precio «antes», tachado: con los decimales de la moneda, como el importe. */
  const [compareAt, setCompareAt] = useState(() => {
    if (!offer?.compareAtPrice) return ''
    const factor = minorUnitFactor(storedPrice?.currency ?? currency)
    return (offer.compareAtPrice / factor).toFixed(factor === 1 ? 0 : 2)
  })
  /*
   * Los otros precios activos: se listan para poder retirarlos, no para
   * editarlos.
   *
   * Una oferta con dos precios a la vez es rara —el alta crea uno— pero existe,
   * y al mudar la edición aquí no podían quedarse sin ningún sitio desde el que
   * quitarlos. Se apuntan y se retiran al guardar, como todo lo demás de este
   * asistente.
   */
  const [droppedPrices, setDroppedPrices] = useState<string[]>([])

  /*
   * Lo de un producto físico: cuántos quedan y cómo llegan.
   *
   * Las existencias son de la oferta y se guardan con ella; vacías es «sin
   * límite», que es lo que viene puesto —contar unidades es opcional—. Las
   * formas de envío son del PROYECTO y se guardan al momento desde su propio
   * editor (ver `ShippingRatesEditor`); aquí sólo se leen para exigir al menos
   * una antes de seguir.
   */
  const physical = type === 'physical'
  const hasBrand = type === 'physical' || type === 'digital'
  const [trackStock, setTrackStock] = useState(offer?.stock !== null && offer?.stock !== undefined)
  const [stock, setStock] = useState(offer?.stock !== null && offer?.stock !== undefined ? String(offer.stock) : '')
  const [shipping, setShipping] = useState<{ rates: ShippingRate[]; maxRates: number } | null>(null)
  const [shippingFailed, setShippingFailed] = useState(false)
  useEffect(() => {
    if (!physical) return
    let alive = true
    void getShipping(projectId, token)
      .then((config) => { if (alive) setShipping({ rates: config.rates, maxRates: config.maxRates }) })
      .catch(() => { if (alive) setShippingFailed(true) })
    return () => { alive = false }
  }, [physical, projectId, token])
  const stockValue = trackStock ? Number.parseInt(stock, 10) : null
  /*
   * Las variantes: un borrador que se guarda con el resto al terminar (ver
   * `VariantsEditor`). Una combinación nueva arranca como el producto: sin
   * límite si no se cuentan existencias, en cero si se cuentan —que no se
   * venda lo que no se ha contado—.
   */
  const [variantsDraft, setVariantsDraft] = useState<VariantsDraft>(() => variantsDraftOf(offer, currency))
  const freshVariant: VariantRow = { stock: trackStock ? '0' : '', sku: '', active: true, price: '', compareAt: '', cost: '', barcode: '', image: '' }
  const variantCombos = combinationsOf(variantsDraft.options)
  const hasVariants = physical && variantCombos.length > 0
  const activeRates = (shipping?.rates ?? []).filter((rate) => rate.active)

  /*
   * Lo que se vende, si es digital: los archivos que ya están y los que se
   * acaban de elegir.
   *
   * Los nuevos se guardan aquí y se suben DESPUÉS de guardar la oferta: la ruta
   * de subida necesita a qué oferta cuelga el archivo, y creando esa oferta
   * todavía no existe. Es el mismo orden que el de publicar.
   *
   * Los que ya estaban se llevan en su propia lista y se borran al guardar los
   * que falten en ella: quitar un archivo del servidor en cuanto se pulsa la
   * papelera haría que cerrar el asistente sin guardar borrara cosas de verdad.
   */
  const [keptFiles, setKeptFiles] = useState<OfferFile[]>(offer?.digitalFiles ?? [])
  /*
   * Los elegidos llevan el nombre aparte del archivo.
   *
   * Un `File` no se renombra —su nombre es de sólo lectura—, así que el nuevo se
   * guarda al lado y se aplica al subir. Y hace falta poder cambiarlo aquí: lo
   * que llega del navegador es como se llamaba en el escritorio de quien lo
   * sube, y «Screenshot 2026-09-06 at 8.46.50 AM.png» es lo primero que lee
   * quien acaba de pagar.
   */
  const [files, setFiles] = useState<{ file: File; name: string }[]>([])
  /** Qué fila está renombrándose, y con qué texto. `id` de guardado o `n:<i>`. */
  const [renaming, setRenaming] = useState<{ key: string; value: string } | null>(null)
  const deliverableRef = useRef<HTMLInputElement>(null)

  // ── Paso 3: qué pasa después ───────────────────────────────────────────────
  const [thankYouMode, setThankYouMode] = useState<Offer['thankYouMode']>(offer?.thankYouMode ?? 'default')
  const [thankYouPageId, setThankYouPageId] = useState(offer?.thankYouPageId ?? '')
  const [thankYouUrl, setThankYouUrl] = useState(offer?.thankYouUrl ?? '')
  /* Las páginas del sitio se piden sólo cuando hacen falta: el asistente se abre
     muchas más veces de las que se elige una página de gracias propia. */
  const [pages, setPages] = useState<PageMeta[] | null>(null)

  // ── Paso 4: revisión ───────────────────────────────────────────────────────
  const [publish, setPublish] = useState(offer?.status === 'active')
  const [publishBlockDialog, setPublishBlockDialog] = useState<'plan' | 'stripe' | null>(null)
  /*
   * Y el tope del catálogo, que es otra cosa que el de Stripe.
   *
   * `blockedByStripe` dice «no puedes cobrar»; esto dice «no te caben más
   * publicadas». Se comprueba aquí y no al guardar porque el servidor
   * respondería 402 DESPUÉS de crear la oferta —el alta no mira el plan, sólo
   * publicar lo hace—, y quien acaba de escribirla entera se llevaría un error
   * por algo que se sabía desde antes de empezar.
   *
   * Editando una que ya está publicada no aplica: ésa ya ocupa su hueco, y
   * contarla otra vez le impediría guardar un cambio de título.
   */
  const [limitDialog, setLimitDialog] = useState(false)
  const alreadyPublished = offer?.status === 'active'
  const publishBlockedByPlan = !alreadyPublished
    && reachedLimit(publishedOffers, SITE_PLAN_LIMITS[plan].offers)

  /*
   * Si se ha tocado algo desde que se abrió.
   *
   * Se serializa todo lo editable y se compara con una foto tomada al montar.
   * Campo a campo serían doce desigualdades que hay que acordarse de ampliar al
   * añadir la trece; así el olvido no existe. Sólo se mira editando: creando
   * siempre hay algo que guardar.
   */
  const shape = JSON.stringify({
    title, description, shortDescription, images, mode, amount, cadence, droppedPrices,
    thankYouMode, thankYouPageId, thankYouUrl, publish, trackStock, stock, variantsDraft, customValues,
    keptFiles: keptFiles.map((row) => `${row.id}:${row.name}`), files: files.map((row) => `${row.name}:${row.file.size}`),
  })
  const [baseline] = useState(() => shape)
  const dirty = shape !== baseline

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  /* Qué campo está mal y por qué, para pintarlo JUNTO al campo. Al pie de un
     paso largo el motivo le queda fuera de pantalla a quien pulsa «Siguiente», y
     lo único que percibe es que el botón no funciona. */
  const [invalid, setInvalid] = useState<{ field: string; message: string } | null>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const amountRef = useRef<HTMLInputElement>(null)
  const urlRef = useRef<HTMLInputElement>(null)

  const stripe = useStripeConnect(projectId, token, (cause) => translateServerError(cause, t('error.unexpected')))

  useEffect(() => {
    if (thankYouMode !== 'page' || pages) return
    let alive = true
    void listPages(token, projectId)
      .then((rows) => { if (alive) setPages(rows) })
      /* Sin lista no se puede elegir, pero tampoco se rompe el alta: el modo
         sigue puesto y el pago cae en la página incluida hasta que se recargue. */
      .catch(() => { if (alive) setPages([]) })
    return () => { alive = false }
  }, [thankYouMode, pages, projectId, token])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || saving) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previous }
  }, [onClose, saving])

  /* Los pasos que existen para ESTA oferta: el del archivo sólo si es digital.
     Y si se cambia el tipo estando en él, hay que salir — el paso desaparece. */
  const steps = useMemo(
    () => ALL_STEPS.filter((entry) => (entry.key !== 'files' || type === 'digital') && (!['variants', 'shipping'].includes(entry.key) || type === 'physical')),
    [type],
  )
  const stepIndex = Math.max(0, steps.findIndex((entry) => entry.key === step))
  useEffect(() => {
    if (!steps.some((entry) => entry.key === step)) setStep('info')
  }, [steps, step])

  /**
   * La copia que cambia según lo que se esté creando.
   *
   * La puerta promete «Producto digital» y el asistente hablaba de «tu oferta»,
   * con un ejemplo de curso en el nombre: el mismo formulario para todo, lo que
   * hace dudar de si se eligió bien. Estas seis frases son las que llevan el
   * peso de decir qué se está montando, y son seis y no una traducción entera
   * porque el resto —el precio, el destino, la revisión— sí es lo mismo.
   */
  const say = (base: string) => (type === 'digital' ? `${base}Digital` : type === 'physical' ? `${base}Physical` : base)

  /** Cuántos archivos va a tener la oferta: los que se quedan más los nuevos. */
  const fileCount = keptFiles.length + files.length

  /** Los precios activos que NO son el que manda. Casi siempre ninguno. */
  const otherPrices = storedPrices.filter((row) => row.id !== storedPrice?.id)

  const chosenCadence = CADENCES.find((row) => row.id === cadence) ?? CADENCES[0]
  const priceInterval: OfferPrice['interval'] = mode === 'subscription' ? chosenCadence.interval : 'one_time'
  const priceIntervalCount = mode === 'subscription' ? chosenCadence.count : 1
  const formattedPreview = previewAmount(amount, currency)
  /* Sin importe escrito la maqueta no AFIRMA un precio: enseña su fantasma. Un
     «0,00» donde nadie ha escrito nada se lee como una decisión tomada. Gratis
     sí es una decisión, así que ahí no hay fantasma que valga. */
  const priceDecided = mode === 'free' || formattedPreview !== ''
  /* Lo gratis no pasa por caja, así que no le falta Stripe: ni el aviso, ni el
     candado de «A la venta». Es la misma regla que aplica el servidor al
     publicar —ver `isFree`—, escrita aquí para poder DECIRLA antes. */
  const blockedByStripe = mode !== 'free' && stripe.ready === false

  // ── El raíl de pasos ───────────────────────────────────────────────────────
  const railRef = useRef<HTMLDivElement>(null)
  /* Dónde va la pastilla del raíl. Medida y no calculada: «Información» y
     «Precio» no miden igual. El mismo gesto que en el asistente de servicios. */
  useLayoutEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const place = () => {
      const active = rail.querySelector<HTMLButtonElement>('button.is-current')
      if (!active) return
      rail.style.setProperty('--step-x', `${active.offsetLeft}px`)
      rail.style.setProperty('--step-w', `${active.offsetWidth}px`)
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(rail)
    return () => observer.disconnect()
  }, [step])

  // ── La cámara de la vista previa ───────────────────────────────────────────
  const [focusZone, setFocusZone] = useState<PreviewZone | null>(null)
  const zoom: PreviewZone = focusZone ?? PREVIEW_FOCUS[step]
  const viewportRef = useRef<HTMLDivElement>(null)
  const wizardRef = useRef<HTMLDivElement>(null)
  useScrollTopOnStep(step, wizardRef)
  const scaleRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const stage = scaleRef.current
    if (!viewport || !stage) return
    const page = stage.firstElementChild as HTMLElement | null
    if (!page) return

    if (zoom === 'full') { stage.style.transform = 'none'; return }
    const target = page.querySelector<HTMLElement>(`[data-zone="${zoom}"]`)
    if (!target) { stage.style.transform = 'none'; return }

    const scale = ZONE_SCALE[zoom]
    /* Del borde de la zona al borde de la página, sumando la cadena de
       posicionados: `.dash-svc-page` es `position: relative`, así que termina
       ahí. Es el mismo cálculo del asistente de servicios, no otro. */
    let left = 0
    let top = 0
    for (let node: HTMLElement | null = target; node && node !== page; node = node.offsetParent as HTMLElement | null) {
      left += node.offsetLeft
      top += node.offsetTop
    }

    /* Centrar la zona sin despegar la página del marco: se puede arrastrar
       hasta que el borde de la imagen toca el del marco, y ni un píxel más. */
    const fit = (start: number, extent: number, frame: number, size: number) => {
      const min = frame / (scale * size) - 1
      const visible = frame / (scale * size)
      /* Una zona MÁS ALTA que lo que se ve no se centra: se apoya al principio,
         o se recorta por los dos lados a la vez y no se ve ni su principio ni
         su final. */
      const wanted = extent >= visible
        ? -start
        : (0.5 * frame) / (scale * size) - (start + extent / 2)
      return Math.min(0, Math.max(min, wanted))
    }

    const tx = fit(left / page.offsetWidth, target.offsetWidth / page.offsetWidth, viewport.clientWidth, page.offsetWidth)
    const ty = fit(top / page.offsetHeight, target.offsetHeight / page.offsetHeight, viewport.clientHeight, page.offsetHeight)
    stage.style.transform = `scale(${scale}) translate(${(tx * 100).toFixed(2)}%, ${(ty * 100).toFixed(2)}%)`
    /* Se recalcula también cuando cambia lo que MIDE la página: una descripción
       larga o una portada mueven todo lo que va debajo. */
  }, [zoom, title, description, shortDescription, image, formattedPreview, priceDecided, type, shipping])

  // ── Validación ─────────────────────────────────────────────────────────────
  /**
   * Si el paso está completo. Devuelve el fallo en vez de sólo un booleano:
   * quien lo llama necesita saber A QUÉ campo llevar el cursor.
   */
  const stepProblem = (key: StepKey): { field: string; message: string } | null => {
    if (key === 'info' && !title.trim()) {
      return { field: 'title', message: t('offers.new.titleRequired') }
    }
    /*
     * El importe es obligatorio en cuanto se cobra.
     *
     * Se podía dejar vacío y seguir, y eso creaba una oferta que existe y no se
     * puede vender: el botón de compra revienta sin precio. Quien elige «pago
     * único» ya ha decidido cobrar; preguntarle cuánto es la consecuencia
     * inmediata de eso, no un trámite que se pueda aplazar.
     *
     * Gratis se salta las dos comprobaciones: ahí el importe es cero y no hay
     * casilla que rellenar.
     */
    if (key === 'price' && mode !== 'free') {
      if (amount.trim() === '') return { field: 'amount', message: t('offers.new.amountRequired') }
      if (toMinorUnits(amount, currency) === null) return { field: 'amount', message: t('offers.new.amountInvalid') }
      if ((toMinorUnits(amount, currency) ?? 0) <= 0) return { field: 'amount', message: t('offers.new.amountZero') }
    }
    /*
     * Un producto digital sin archivo no es un producto digital.
     *
     * Se podía pasar de largo y subirlo después desde la ficha, y lo que salía
     * de ahí era una oferta que no puede entregar nada: publicada, cobra; y
     * quien paga llega a una página de descarga vacía. El paso existe justo para
     * evitar eso, así que aquí se para.
     *
     * Cuenta lo que va a tener, no lo que ya está subido: al crear no hay nada
     * subido todavía y lo elegido cuenta igual.
     */
    if (key === 'files' && type === 'digital' && fileCount === 0) {
      return { field: 'file', message: t('offers.new.file.required') }
    }
    if (key === 'files' && type === 'digital' && fileCount > MAX_OFFER_FILES) {
      return { field: 'file', message: t('offers.detail.files.full') }
    }
    if (key === 'variants' && physical) {
      const final = finalizeVariants(variantsDraft, freshVariant, currency)
      if (!final.ok) return { field: 'variants', message: t(final.error, final.vars) }
    }
    if (key === 'shipping' && physical) {
      if (!hasVariants && trackStock && (!/^\d+$/.test(stock.trim()) || (stockValue ?? -1) < 0)) return { field: 'stock', message: t('offers.new.shipping.stockInvalid') }
      /* Sin ninguna forma de envío activa el pago no sabría cómo llega: el
         servidor no deja publicarlo, así que se dice aquí, donde se arregla. */
      if (shipping && activeRates.length === 0) return { field: 'rates', message: t('offers.new.shipping.ratesRequired') }
    }
    if (key === 'delivery' && thankYouMode === 'page' && !thankYouPageId) {
      return { field: 'thankYouPage', message: t('offers.new.after.pageMissing') }
    }
    if (key === 'delivery' && thankYouMode === 'url' && !/^https:\/\/\S+$/i.test(thankYouUrl.trim())) {
      return { field: 'thankYouUrl', message: t('offers.new.after.urlInvalid') }
    }
    return null
  }

  const focusField = (field: string) => {
    if (field === 'title') titleRef.current?.focus()
    if (field === 'amount') amountRef.current?.focus()
    if (field === 'thankYouUrl') urlRef.current?.focus()
  }

  const next = () => {
    const problem = stepProblem(step)
    if (problem) { setInvalid(problem); focusField(problem.field); return }
    /* Lo escrito en el campo de valores sin pulsar Intro también cuenta. */
    if (step === 'variants') setVariantsDraft(commitPendingVariants)
    setInvalid(null)
    setError('')
    setStep(steps[Math.min(steps.length - 1, stepIndex + 1)]?.key ?? step)
  }

  const goTo = (key: StepKey) => { setInvalid(null); setError(''); setVariantsDraft(commitPendingVariants); setStep(key) }

  // ── Guardar ────────────────────────────────────────────────────────────────
  /**
   * Crea la oferta, o guarda la que se está editando.
   *
   * El mismo camino para las dos, y en el mismo orden: primero lo que va en la
   * fila de la oferta —lo que no puede quedar a medias—, después el precio, los
   * archivos y por último publicar. Cada uno de los tres últimos puede fallar
   * por su cuenta sin llevarse por delante lo anterior; con todo en una sola
   * petición, un archivo que no cabe en la cuota tiraría también el nombre que
   * se acaba de escribir.
   */
  const create = async () => {
    if (saving) return
    /* Si algo falta, se va al paso donde falta y se vuelve a validar en el
       fotograma siguiente: hasta que React no lo pinta, el campo al que hay que
       llevar el cursor todavía no está en el DOM. */
    const firstBad = steps
      .filter((entry) => entry.key !== 'review')
      .map((entry) => [entry.key, stepProblem(entry.key)] as const)
      .find(([, problem]) => problem)
    if (firstBad) {
      const [key, problem] = firstBad
      setStep(key)
      setInvalid(problem)
      requestAnimationFrame(() => { focusField(problem!.field) })
      return
    }

    setSaving(true)
    setError('')
    try {
      /* Gratis se guarda como un precio de CERO y no como «sin precio»: sin
         precio la oferta no se puede vender —el botón revienta al pulsarlo— y
         gratis sí se entrega. Ver `isFree` en el worker, que es quien decide a
         partir de ese cero que no hay que abrir Stripe. */
      /* Aquí ya no hay «sin precio»: el paso no deja pasar sin importe salvo
         que sea gratis, y gratis es un cero. */
      const minor = mode === 'free' ? 0 : toMinorUnits(amount, currency)
      /* Sólo en pago único y si de verdad es más caro; si no, se borra. */
      const compareMinor = mode === 'one_time' && compareAt.trim() ? toMinorUnits(compareAt, currency) : null
      const compareAtPrice = compareMinor !== null && minor !== null && compareMinor > minor ? compareMinor : null
      /* El precio va en la MISMA llamada que el alta: así la oferta nunca existe
         un instante sin él. Sin importe se crea sin precio, que es legítimo —lo
         único que no podrá es venderse hasta que lo tenga—. */
      /* Editando, el destino del comprador viaja con el resto: son columnas de
         la misma fila y `updateOffer` las acepta todas, así que no hay motivo
         para dos peticiones. En el alta va aparte porque `createOffer` no las
         acepta. */
      let saved: Offer = offer
        ? await updateOffer(projectId, offer.id, {
          title: title.trim(),
          ...(hasBrand ? { brand: brand.trim() } : {}),
          tags,
          compareAtPrice,
          description: description.trim(),
          shortDescription: shortDescription.trim(),
          /* Sin `type`: no se edita, así que mandarlo sería escribir en una
             columna que nadie ha tocado. */
          images,
          ...(physical ? { stock: stockValue } : {}),
          thankYouMode,
          thankYouPageId: thankYouMode === 'page' ? thankYouPageId : null,
          thankYouUrl: thankYouMode === 'url' ? thankYouUrl.trim() : null,
          /* Pasó por todos los pasos (la validación de arriba lo exige): deja
             de estar «por completar» y ya se puede activar. */
          ...(completing ? { setupPending: false as const } : {}),
          customValues,
        }, token)
        : await createOffer(projectId, {
          title: title.trim(),
          ...(hasBrand ? { brand: brand.trim() } : {}),
          ...(tags.length ? { tags } : {}),
          ...(compareAtPrice ? { compareAtPrice } : {}),
          description: description.trim(),
          shortDescription: shortDescription.trim(),
          type,
          ...(physical ? { stock: stockValue } : {}),
          ...(images.length > 0 ? { images } : {}),
          ...(minor !== null
            ? { price: { unitAmount: minor, currency, interval: priceInterval, intervalCount: priceIntervalCount } }
            : {}),
        }, token)

      /*
       * El precio, cuando se edita: el nuevo entra y el viejo sale, en ese
       * orden.
       *
       * Y ese orden importa: el servidor se niega a retirar el ÚNICO precio
       * activo de una oferta publicada, así que al revés la edición fallaba a
       * mitad y dejaba sin precio algo que estaba a la venta. Es el mismo baile
       * que hace el asistente de servicios reservables.
       *
       * Sólo si de verdad cambia algo —importe o cadencia—: repetirlo sin
       * cambios daría de alta en Stripe un precio idéntico cada vez que alguien
       * abre el asistente a corregir una coma de la descripción.
       */
      if (offer && minor !== null) {
        const current = saved.prices.find((row) => row.active && row.isDefault) ?? saved.prices.find((row) => row.active)
        const same = current
          && current.unitAmount === minor
          && current.interval === priceInterval
          && (current.intervalCount || 1) === priceIntervalCount
        if (!same) {
          saved = await addOfferPrice(
            projectId,
            saved.id,
            // La moneda la decide el servidor con el ajuste de región; va aquí
            // sólo para que el panel y lo guardado digan lo mismo sin recargar.
            { unitAmount: minor, currency, interval: priceInterval, intervalCount: priceIntervalCount, isDefault: true },
            token,
          )
          if (current) saved = await removeOfferPrice(projectId, saved.id, current.id, token)
        }
        /* Y los que se hayan marcado de la lista de los demás. Después del
           relevo, para que retirar el penúltimo no deje a la oferta sin
           ninguno en mitad del camino. */
        for (const priceId of droppedPrices) {
          if (saved.prices.some((row) => row.id === priceId && row.active)) {
            saved = await removeOfferPrice(projectId, saved.id, priceId, token)
          }
        }
      }

      /*
       * Los archivos, en cuanto la oferta existe.
       *
       * Aparte y con su propio try: si una subida falla —se cae la red, no cabe
       * en la cuota— la oferta ya está guardada con todo lo demás, y perderla
       * por eso sería tirar el trabajo. Se dice qué pasó y se entrega igual; el
       * archivo se vuelve a intentar abriendo otra vez el asistente.
       *
       * Primero las bajas y después las altas: al revés, quien sustituye un PDF
       * por otro toca el tope de cinco a mitad de camino.
       */
      /* Y sólo si sigue siendo digital: se puede elegir el archivo y cambiar
         después el tipo, y ahí el paso del archivo desaparece —pero lo elegido
         seguía en memoria—. Subirlo entonces le colgaría un entregable a algo
         que no se descarga. Las bajas sí se aplican pase lo que pase: dejar
         atrás un archivo que se pidió quitar es peor. */
      if (offer) {
        for (const stored of offer.digitalFiles) {
          const kept = keptFiles.find((row) => row.id === stored.id)
          try {
            if (!kept) saved = await removeOfferFile(projectId, saved.id, stored.id, token)
            /* Renombrar es su propia petición y no viaja con nada más: cambia
               una fila del JSON de archivos, no la oferta. */
            else if (kept.name !== stored.name) saved = await renameOfferFile(projectId, saved.id, stored.id, kept.name, token)
          } catch (fileError) {
            setError(translateServerError(fileError, t('error.unexpected')))
            setSaving(false)
            onCreated(saved)
            return
          }
        }
      }

      if (type === 'digital') {
        for (const picked of files) {
          try {
            /* Con el nombre que se le haya puesto aquí: el servidor guarda el
               del archivo que le llega, así que el cambio se hace envolviéndolo
               en uno nuevo —el contenido es el mismo, sin copiarlo—. */
            const named = picked.name && picked.name !== picked.file.name
              ? new File([picked.file], picked.name, { type: picked.file.type })
              : picked.file
            saved = await uploadOfferFile(projectId, saved.id, named, token)
          } catch (uploadError) {
            setError(translateServerError(uploadError, t('error.unexpected')))
            setSaving(false)
            onCreated(saved)
            return
          }
        }
      }

      /*
       * Las variantes, con la oferta ya existiendo: van por su propia ruta
       * porque reemplazan todas las combinaciones de una vez. Sólo si cambió
       * algo, y con su propio try por lo mismo que los archivos: si fallan, lo
       * demás ya quedó guardado.
       */
      if (physical && variantsChanged(variantsDraft, offer, freshVariant, currency)) {
        const final = finalizeVariants(variantsDraft, freshVariant, currency)
        if (final.ok) {
          try {
            saved = await saveOfferVariants(projectId, saved.id, { options: final.options, variants: final.variants }, token)
          } catch (variantsError) {
            setError(translateServerError(variantsError, t('error.unexpected')))
            setSaving(false)
            onCreated(saved)
            return
          }
        }
      }

      /* El destino del comprador va aparte porque `createOffer` no lo acepta:
         son columnas que sólo se escriben con PATCH. Sólo se manda cuando NO es
         el de por omisión — una petición para dejar algo como ya estaba es una
         forma gratuita de que falle el alta. Editando ya viajó con el resto,
         arriba. */
      /* Los campos personalizados, también aparte en el alta: `createOffer` no
         los acepta. Sólo si se escribió alguno. */
      if (!offer && Object.values(customValues).some((value) => value.trim())) {
        saved = await updateOffer(projectId, saved.id, { customValues }, token)
      }

      if (!offer && thankYouMode !== 'default') {
        saved = await updateOffer(projectId, saved.id, {
          thankYouMode,
          thankYouPageId: thankYouMode === 'page' ? thankYouPageId : null,
          thankYouUrl: thankYouMode === 'url' ? thankYouUrl.trim() : null,
        }, token)
      }

      /*
       * Publicar es un paso APARTE, y a propósito.
       *
       * El servidor puede negarse —vender en línea necesita Stripe conectado— y
       * si eso tumbara la creación entera, quien no tiene Stripe perdería todo
       * lo que acaba de rellenar por haber tocado un interruptor. Así la oferta
       * queda guardada en borrador y lo único que falla es publicarla, con su
       * motivo delante.
       */
      /*
       * Editando, el interruptor va en los dos sentidos: apagarlo en algo que
       * está a la venta lo retira. Creando sólo puede encenderlo, porque nace
       * en borrador.
       *
       * Una oferta RETIRADA se queda como está pase lo que pase: el interruptor
       * nace apagado —no está publicada— y aplicarlo sin más la sacaría del
       * archivo por sorpresa, que no es lo que vino a hacer quien abrió esto a
       * corregir una descripción. Volver a ponerla en pie se pide en su ficha.
       */
      const wantedStatus: Offer['status'] = publish ? 'active' : 'draft'
      if (saved.status !== 'archived' && saved.status !== wantedStatus && (publish || editing)) {
        try {
          saved = await updateOffer(projectId, saved.id, { status: wantedStatus }, token)
        } catch (publishError) {
          setError(translateServerError(publishError, t('error.unexpected')))
          setSaving(false)
          /* Se entrega igual: existe y lo demás quedó guardado, y perderla por
             no poder publicarla sería castigar a quien la escribió por una
             condición que no controla. */
          onCreated(saved)
          return
        }
      }
      onCreated(saved)
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
      setSaving(false)
    }
  }

  const chosenPage = (pages ?? []).find((page) => page.id === thankYouPageId)
  const billingLabel = mode === 'free'
    ? t('offers.new.freeTitle')
    : formattedPreview
      /* La cadencia elegida, sin traducir a mano cada caso: son cinco y la
         etiqueta ya vive en la tabla. */
      ? `${formattedPreview} · ${t(mode === 'subscription' ? chosenCadence.labelKey : 'offers.new.review.once')}`
      : t('offers.noPrice')
  const afterLabel = thankYouMode === 'page'
    ? (chosenPage?.name ?? t('offers.detail.thankYou.page'))
    : thankYouMode === 'url'
      ? thankYouUrl.trim()
      : t('offers.detail.thankYou.default')

  return createPortal(
    <div
      ref={wizardRef}
      className="dash-svc-wizard dash-ofw"
      role="dialog"
      aria-modal="true"
      aria-label={editing ? t('offers.edit.dialogAria', { name: offer?.title ?? '' }) : t('offers.new.dialogAria')}
    >
      {/* La misma barra que «Página nueva», reducida a lo que no dice ya el
          raíl: marca, menú de área y la salida. El raíl va DENTRO, en medio
          —suelto debajo eran dos franjas seguidas del mismo alto antes de la
          primera pregunta— y como enseña el paso con su nombre, el título de la
          pantalla y el «Paso 2 de 4» que iban a los lados sobraban: tres cosas
          para decir lo mismo, ocupando el ancho que necesita el raíl.

          El nombre de la oferta tampoco está aquí: se pide en su paso, encima
          de la descripción, que es de lo que es hermano. */}
      <WizardTopBar
        onBack={onClose}
        center={(
        <nav className="dash-svc-wizard-progress" aria-label={t('offers.new.progressAria')}>
          <div className="dash-svc-wizard-rail" ref={railRef}>
            <span className="dash-svc-wizard-rail-thumb" aria-hidden="true" />
            {/*
              Creando: hacia atrás siempre, hacia adelante nunca. Un paso de más
              adelante depende de decisiones que aún no se han tomado —el resumen,
              de todas—, y dejar entrar ahí enseña una pantalla a medias.

              Editando ya existe todo, así que el orden deja de ser una secuencia y
              pasa a ser un índice: se entra a arreglar una cosa concreta, que es
              justo lo que hacen los atajos de la ficha.
            */}
            {steps.map(({ key, labelKey, Icon }, index) => {
              const reachable = (editing && !completing) || index < stepIndex
              return (
                <button
                  type="button"
                  key={key}
                  className={`${index === stepIndex ? 'is-current' : ''}${index < stepIndex ? ' is-done' : ''}`}
                  disabled={!reachable && index !== stepIndex}
                  onClick={() => { if (reachable) goTo(key) }}
                >
                  {/* La palomita sustituye al icono en los ya hechos, y sólo al
                      crear: ahí sí hay un avance que contar. Editando los pasos
                      existen todos desde el principio, así que ninguno está
                      «hecho» y todos enseñan lo que son. */}
                  <span>{(!editing || completing) && index < stepIndex ? <IconCheck size={13} /> : <Icon size={14} />}</span>
                  {/* En <em> para poder esconderla cuando la ventana se estrecha:
                      con el «Paso 2 de 4» de la barra de arriba, los iconos bastan. */}
                  <em>{t(labelKey)}</em>
                </button>
              )
            })}
          </div>
        </nav>
        )}
        {...(editing && dirty ? {
          closeHidden: true,
          actions: (
            <PendingChangesBar
              saving={saving}
              discardLabel={t('offers.new.cancel')}
              saveLabel={t('offers.edit.save')}
              savingLabel={t('offers.edit.saving')}
              onDiscard={onClose}
              onSave={() => { void create() }}
            />
          ),
        } : {})}
        onClose={onClose}
        busy={saving}
      />


      <div className="dash-svc-wizard-body">
        <form
          className="dash-svc-wizard-form"
          onSubmit={(event) => {
            event.preventDefault()
            if (stepIndex < steps.length - 1) next(); else void create()
          }}
        >
          <div className="dash-svc-wizard-form-inner">
            {/* Sólo en el primer paso: dicho una vez basta, y en cada paso
                tapaba lo que se estaba rellenando. */}
            {completing && stepIndex === 0 ? (
              <p className="dash-ai-pending-note">
                <VelticaMascot outline size={17} />
                <span>{t('aiPending.wizardNote')}</span>
              </p>
            ) : null}
            {/* ── 1. Qué vendes ──────────────────────────────────────────── */}
            {step === 'info' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t(say('offers.new.info.kicker'))}</span>
                <h2>{t(say('offers.new.info.title'))}</h2>
                <p>{t(say('offers.new.info.lead'))}</p>

                {/* Nombre y descripción comparten caja: son el mismo dato —qué
                    es esto— escrito en corto y en largo. */}
                <div className="dash-svc-group">
                  <label className={`dash-svc-field${invalid?.field === 'title' ? ' is-invalid' : ''}`}>
                    <span>{t('offers.new.titleLabel')} <b>*</b></span>
                    <input
                      ref={titleRef}
                      autoFocus
                      maxLength={200}
                      value={title}
                      placeholder={t(say('offers.new.titlePlaceholder'))}
                      aria-invalid={invalid?.field === 'title' || undefined}
                      aria-describedby={invalid?.field === 'title' ? 'ofw-err-title' : undefined}
                      onFocus={() => setFocusZone('title')}
                      onBlur={() => setFocusZone(null)}
                      onChange={(event) => { setTitle(event.target.value); if (invalid?.field === 'title') setInvalid(null) }}
                    />
                    {invalid?.field === 'title'
                      ? <small className="dash-svc-field-error" id="ofw-err-title">{invalid.message}</small>
                      : <small>{t('offers.new.titleFoot')}</small>}
                  </label>
                  <OfferDescriptionFields shortDescription={shortDescription} description={description}
                    onShortDescriptionChange={setShortDescription} onDescriptionChange={setDescription} onFocus={setFocusZone} />
                  {/* La marca, sólo en lo que se fabrica o se edita: un servicio
                      no la tiene. Opcional; las colecciones la usan. */}
                  {hasBrand ? (
                    <div className="dash-svc-field dash-offer-brand-field">
                      <span>{t('offers.new.brandLabel')}</span>
                      <OfferBrandField projectId={projectId} token={token} value={brand} known={brands} onChange={setBrand} {...(onBrandRemoved ? { onRemovedEverywhere: onBrandRemoved } : {})} />
                      <small>{t('offers.new.brandFoot')}</small>
                    </div>
                  ) : null}
                  {/* Las etiquetas, para todas: las colecciones las usan. */}
                  <div className="dash-svc-field dash-offer-brand-field">
                    <span>{t('offers.tags.title')}</span>
                    <OfferTagsField projectId={projectId} token={token} value={tags} known={knownTagList} onChange={setTags} {...(onTagRemoved ? { onRemovedEverywhere: onTagRemoved } : {})} />
                    <small>{t('offers.new.tagsFoot')}</small>
                  </div>

                </div>

                {/* Las imágenes, del mismo almacén que todo lo demás del panel:
                    subir un archivo suelto aquí dejaba fotos que no se podían
                    reutilizar en ningún otro sitio.

                    La primera es la portada y así se dice, con su distintivo:
                    es la que sale en la página de pago y en la lista, y sin
                    decirlo el orden parecía casual. Las demás acompañan. */}
                <div className="dash-svc-group">
                  <div className="dash-svc-image-field">
                    <div><strong>{t('gallery.label')}</strong><span>{t('offers.new.optional')}</span></div>
                    <OfferGalleryField
                      token={token}
                      projectId={projectId}
                      images={images}
                      onChange={setImages}
                      onFocusMedia={(focused) => setFocusZone(focused ? 'media' : null)}
                    />
                  </div>
                </div>
              </section>
            ) : null}

            {/* ── 2. Cuánto cuesta ───────────────────────────────────────── */}
            {step === 'files' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('offers.new.files.kicker')}</span>
                <h2>{t('offers.new.files.title')}</h2>
                <p>{t('offers.new.files.lead')}</p>

                <div className="dash-svc-setting-block">
                  <label>{t('offers.new.file.label')} <b>*</b></label>
                  <span className="dash-svc-block-hint">{t('offers.new.file.hint')}</span>

                  {/*
                    Cada archivo, como una pieza y no como un renglón.

                    Era una fila de lista: icono pequeño, nombre y papelera. Para
                    algo que ES lo que se vende —y de lo que hay uno o dos, no
                    cuarenta— se leía como un adjunto de correo. Ahora lleva su
                    dibujo con el color de su tipo, el nombre a tamaño de nombre
                    y debajo la ficha técnica: formato, peso y si ya está en el
                    servidor o todavía va a subir al guardar.

                    Y el nombre se puede cambiar aquí, que es lo que faltaba: es
                    lo primero que lee quien acaba de pagar, y lo que llega del
                    navegador es como se llamara en el escritorio de quien lo
                    sube.
                  */}
                  {fileCount > 0 ? (
                    <ul className="dash-ofw-files">
                      {keptFiles.map((stored) => (
                        <FileCard
                          key={stored.id}
                          name={stored.name}
                          size={stored.size}
                          contentType={stored.contentType}
                          pendingLabel={null}
                          renaming={renaming?.key === stored.id ? renaming.value : null}
                          onRenameStart={() => setRenaming({ key: stored.id, value: stored.name })}
                          onRenameChange={(value) => setRenaming({ key: stored.id, value })}
                          onRenameCancel={() => setRenaming(null)}
                          onRenameCommit={(value) => {
                            const clean = value.trim()
                            /* Vacío no es un nombre: se deshace y se queda el que tenía. */
                            if (clean) setKeptFiles((rows) => rows.map((row) => (row.id === stored.id ? { ...row, name: clean } : row)))
                            setRenaming(null)
                          }}
                          onRemove={() => setKeptFiles((rows) => rows.filter((row) => row.id !== stored.id))}
                        />
                      ))}
                      {files.map((picked, index) => (
                        <FileCard
                          key={`${picked.file.name}-${picked.file.size}-${index}`}
                          name={picked.name}
                          size={picked.file.size}
                          contentType={picked.file.type}
                          pendingLabel={t('offers.edit.file.pending')}
                          renaming={renaming?.key === `n:${index}` ? renaming.value : null}
                          onRenameStart={() => setRenaming({ key: `n:${index}`, value: picked.name })}
                          onRenameChange={(value) => setRenaming({ key: `n:${index}`, value })}
                          onRenameCancel={() => setRenaming(null)}
                          onRenameCommit={(value) => {
                            const clean = value.trim()
                            if (clean) setFiles((rows) => rows.map((row, at) => (at === index ? { ...row, name: clean } : row)))
                            setRenaming(null)
                          }}
                          onRemove={() => setFiles((rows) => rows.filter((_, at) => at !== index))}
                        />
                      ))}
                    </ul>
                  ) : null}

                  {fileCount < MAX_OFFER_FILES ? (
                    <button
                      type="button"
                      className={`dash-svc-image-drop${fileCount > 0 ? ' is-compact' : ''}`}
                      onClick={() => deliverableRef.current?.click()}
                    >
                      <IconUpload size={fileCount > 0 ? 18 : 20} />
                      <span>
                        <strong>{t(fileCount > 0 ? 'offers.detail.files.add' : 'offers.new.file.pick')}</strong>
                        <small>{t('offers.detail.files.hint')}</small>
                      </span>
                    </button>
                  ) : (
                    <p className="dash-offer-card-foot">{t('offers.detail.files.full')}</p>
                  )}
                  <input
                    ref={deliverableRef}
                    type="file"
                    hidden
                    /* De varios en varios: subir seis PDF de uno en uno son seis
                       viajes al selector de archivos para una sola decisión. */
                    multiple
                    onChange={(event) => {
                      const picked = Array.from(event.target.files ?? [])
                      event.target.value = ''
                      if (picked.length === 0) return
                      /* El tope de verdad lo pone el servidor; aquí se avisa
                         antes para no mandar quince megas y que los rechace al
                         llegar. */
                      if (picked.some((row) => row.size > MAX_OFFER_FILE_BYTES)) {
                        setInvalid({ field: 'file', message: t('offers.new.file.tooLarge') })
                        return
                      }
                      setInvalid(null)
                      /* Y el de cuántos caben se aplica aquí en vez de rechazar
                         la tanda entera: se cogen los que quepan. */
                      setFiles((rows) => [
                        ...rows,
                        ...picked.map((file) => ({ file, name: file.name })),
                      ].slice(0, MAX_OFFER_FILES - keptFiles.length))
                    }}
                  />
                  {invalid?.field === 'file' ? <small className="dash-svc-field-error">{invalid.message}</small> : null}
                </div>

              </section>
            ) : null}

            {step === 'price' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('offers.new.price.kicker')}</span>
                <h2>{t('offers.new.price.title')}</h2>
                <p>{t('offers.new.price.lead')}</p>
                {completing ? <p className="dash-ai-pending-hint">{t('aiPending.priceHint')}</p> : null}

                <div className="dash-svc-setting-block">
                  <label>{t('offers.new.payType')}</label>
                  <div className="dash-svc-choices">
                    {([
                      ['free', IconGift, 'offers.new.freeTitle', 'offers.new.freeBody'],
                      ['one_time', IconCoin, 'offers.new.oneTimeTitle', 'offers.new.oneTimeBody'],
                      ['subscription', IconRepeat, 'offers.new.subscriptionTitle', 'offers.new.subscriptionBody'],
                    ] as const).filter(([value]) => !physical || value === 'one_time').map(([value, Icon, title, body]) => (
                      <button key={value} type="button" className={mode === value ? 'is-on' : ''} onClick={() => { setMode(value); setInvalid(null) }}>
                        <span className="dash-svc-choice-mark">{mode === value ? <IconCheck size={12} /> : null}</span>
                        <span className="dash-svc-choice-copy">
                          <strong><Icon size={14} aria-hidden="true" /> {t(title)}</strong>
                          <small>{t(body)}</small>
                        </span>
                      </button>
                    ))}
                  </div>

                </div>

                {/* Gratis no tiene importe que escribir, así que la caja entera
                    desaparece. Dejarla vacía —o en cero y apagada— sería enseñar
                    un campo que no hay que rellenar y hacer dudar de si falta
                    algo. */}
                {mode !== 'free' ? (
                <>
                {/* El importe, en su propia caja y no debajo del tipo de pago.
                    Compartiendo caja, «Tipo de pago» y «Precio» salían con la
                    misma pinta —dos rótulos negros seguidos— y el segundo se
                    leía como un campo más del primero en vez de como la otra
                    decisión que hay aquí. */}
                <div className="dash-svc-setting-block">
                  <label htmlFor="ofw-amount">{t('offers.price.amount')} <b>*</b></label>
                  <span className="dash-svc-block-hint">{t('offers.new.amountFoot')}</span>
                  {/* Importe, moneda y periodicidad dentro de UN solo recuadro:
                      son las tres partes de la misma frase —«199 MXN al mes»— y
                      sueltas se leían como un campo y dos etiquetas al lado. */}
                  <div className={`dash-ofw-amount${invalid?.field === 'amount' ? ' is-invalid' : ''}`}>
                    <CurrencySymbol currency={currency} />
                    <input
                      id="ofw-amount"
                      ref={amountRef}
                      inputMode="decimal"
                      value={amount}
                      placeholder="199.00"
                      aria-label={t('offers.new.amountAria')}
                      aria-invalid={invalid?.field === 'amount' || undefined}
                      aria-describedby={invalid?.field === 'amount' ? 'ofw-err-amount' : undefined}
                      onFocus={() => setFocusZone('price')}
                      onBlur={() => setFocusZone(null)}
                      onChange={(event) => { setAmount(event.target.value); if (invalid?.field === 'amount') setInvalid(null) }}
                    />
                    <span className="dash-ofw-amount-currency" title={t('offers.price.currencyTitle')}>
                      {currency.toUpperCase()}
                    </span>
                    {/*
                      Cada cuánto se cobra, pegado al importe y como lista.

                      Estaba debajo, en su propia caja y como cinco pastillas:
                      ocupaba el ancho entero para una decisión que es la segunda
                      mitad de la frase que se acaba de escribir —«199 MXN» AL
                      MES— y obligaba a bajar la vista para terminarla. Aquí se
                      lee de corrido, y una lista de cinco no necesita enseñar
                      las cinco a la vez para dejar claro que se puede cambiar.
                    */}
                    {mode === 'subscription' ? (
                      <select
                        className="dash-ofw-amount-interval"
                        value={cadence}
                        aria-label={t('offers.new.cadence.label')}
                        onChange={(event) => setCadence(event.target.value as CadenceId)}
                      >
                        {CADENCES.map((row) => <option key={row.id} value={row.id}>{t(row.labelKey)}</option>)}
                      </select>
                    ) : null}
                  </div>
                  {invalid?.field === 'amount'
                    ? <small className="dash-svc-field-error" id="ofw-err-amount">{invalid.message}</small>
                    : null}
                </div>

                {/* El «antes» tachado, como el «Compare-at price» de Shopify. Sólo
                    en pago único: en un cobro recurrente se lee raro. */}
                {mode === 'one_time' ? (() => {
                  const now = toMinorUnits(amount, currency)
                  const before = compareAt.trim() ? toMinorUnits(compareAt, currency) : null
                  const off = now !== null && before !== null && before > now ? Math.round((1 - now / before) * 100) : null
                  const tooLow = before !== null && now !== null && before <= now
                  return (
                    <div className="dash-svc-setting-block">
                      <label htmlFor="ofw-compare">{t('offers.new.compareAt')}</label>
                      <span className="dash-svc-block-hint">{t('offers.new.compareAtFoot')}</span>
                      <div className="dash-ofw-amount">
                        <CurrencySymbol currency={currency} />
                        <input id="ofw-compare" inputMode="decimal" value={compareAt} placeholder="249.00" aria-label={t('offers.new.compareAt')} onChange={(event) => setCompareAt(event.target.value)} />
                        <span className="dash-ofw-amount-currency">{currency.toUpperCase()}</span>
                      </div>
                      {tooLow
                        ? <small className="dash-svc-field-error">{t('offers.new.compareAtLow')}</small>
                        : off !== null ? <small className="dash-svc-block-hint">{t('offers.new.compareAtOff', { percent: off })}</small> : null}
                    </div>
                  )
                })() : null}

                </>
                ) : null}

                {/*
                  Los demás precios activos, si los hay.

                  Lo de arriba es EL precio —el que usa el botón de compra— y
                  cambiarlo sustituye ése. Una oferta puede arrastrar más de uno,
                  y sin esta lista no habría dónde retirarlos desde que la ficha
                  dejó de tener su pestaña. No se añaden desde aquí: dos precios
                  a la vez es un caso raro, y ofrecerlo en mitad de un alta
                  invita a crearlo sin necesitarlo.
                */}
                {otherPrices.length > 0 ? (
                  <div className="dash-svc-setting-block">
                    <label>{t('offers.edit.otherPrices')}</label>
                    <span className="dash-svc-block-hint">{t('offers.edit.otherPricesHint')}</span>
                    <ul className="dash-offer-price-list">
                      {otherPrices.map((row) => {
                        const dropped = droppedPrices.includes(row.id)
                        return (
                          <li key={row.id} className={dropped ? 'is-dropped' : ''}>
                            <div className="dash-offer-price-copy">
                              <strong>{row.formatted}</strong>
                              <span>{t(cadenceKeyOf(row))} · {row.currency.toUpperCase()}</span>
                            </div>
                            {/* Marcar y desmarcar con el mismo botón: se retira
                                al guardar, así que hasta entonces tiene que
                                poder deshacerse sin cerrar el asistente. */}
                            <button
                              type="button"
                              className="dash-icon-btn"
                              aria-label={t(dropped ? 'offers.edit.keepPriceAria' : 'offers.price.removeAria', { price: row.formatted })}
                              data-tooltip={t(dropped ? 'offers.edit.keepPrice' : 'offers.price.remove')}
                              onClick={() => setDroppedPrices((rows) => (dropped ? rows.filter((id) => id !== row.id) : [...rows, row.id]))}
                            >
                              {dropped ? <IconArrowLeft size={14} /> : <IconTrash size={14} />}
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ) : null}

                {/*
                 * Falta Stripe, y se dice AQUÍ, entero, que es donde se decide
                 * cobrar.
                 *
                 * Estaba en la revisión, dos pasos más allá: quien elegía el
                 * importe no se enteraba de nada y se encontraba el interruptor
                 * de «A la venta» apagado al final, sin haber visto en ningún
                 * momento de dónde salía esa condición. El sitio de un aviso es
                 * donde nace lo que avisa.
                 *
                 * Y no bloquea: se puede seguir, terminar el alta y dejar la
                 * oferta escrita. Lo único que espera es el cobro.
                 */}
                {/*
                 * Dos frenos distintos, dos avisos distintos.
                 *
                 * Falta la CUENTA: es un trámite, se resuelve aquí mismo y el
                 * aviso lleva la marca de quien lo pide, con su botón. Falta el
                 * PLAN: no es un trámite, es algo que todavía no has comprado, y
                 * lo que hay que enseñar es lo que se gana con él — no una
                 * gestión de Stripe con un botón de planes pegado, que es lo que
                 * había y leía como una avería en vez de como una oferta.
                 *
                 * La misma tarjeta de Cobros y de Ventas, en su forma corta:
                 * titular, una frase y el botón. Las pastillas y la letra
                 * pequeña son el detalle de quien ya se paró a leer, y aquí se
                 * está en mitad de un alta.
                 */}
                {blockedByStripe ? (
                  canConnectStripe ? (
                    <Notice
                      tone="brand"
                      icon={<StripeMark size={17} />}
                      title={t('offers.new.stripe.publishTitle')}
                      actions={(
                        <button
                          type="button"
                          className="dash-secondary-btn"
                          disabled={stripe.connecting}
                          onClick={() => { void stripe.connect() }}
                        >
                          {stripe.connecting ? <IconLoader2 size={14} className="dash-spin" /> : <StripeMark size={14} />}
                          {t(stripe.connecting ? 'offers.new.stripe.connecting' : 'offers.new.stripe.connect')}
                        </button>
                      )}
                    >
                      <p>{t('offers.new.stripe.reviewBody')}</p>
                    </Notice>
                  ) : (
                    <UpsellCard slim standalone copy={PAYMENTS_UPSELL} onOpenPlans={handleOpenPlans} />
                  )
                ) : null}
              </section>
            ) : null}

            {/* ── Envío (sólo productos físicos) ─────────────────────────── */}
            {/* ── Variantes (sólo productos físicos) ─────────────────────── */}
            {step === 'variants' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('offers.new.variants.kicker')}</span>
                <h2>{t('offers.new.variants.title')}</h2>
                <p>{t('offers.new.variants.lead')}</p>
                <div className="dash-vx-step">
                  <VariantsEditor projectId={projectId} token={token} currency={currency} baseAmount={toMinorUnits(amount, currency)} skuBase={title} basePrice={formattedPreview} photos={images} draft={variantsDraft} onChange={(draft) => { setVariantsDraft(draft); if (invalid?.field === 'variants') setInvalid(null) }} fresh={freshVariant} />
                  {invalid?.field === 'variants' ? <small className="dash-svc-field-error" role="alert">{invalid.message}</small> : null}
                </div>
              </section>
            ) : null}

            {step === 'shipping' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('offers.new.shipping.kicker')}</span>
                <h2>{t('offers.new.shipping.title')}</h2>
                <p>{t('offers.new.shipping.lead')}</p>

                {/* Existencias: opcionales. Sin contarlas se vende sin límite,
                    que es lo que hace quien fabrica bajo pedido. Con variantes
                    se cuentan por combinación, en la tarjeta «Variantes» de la
                    ficha, y aquí sólo se dice. */}
                {hasVariants ? (
                  <div className="dash-svc-setting-block">
                    <label>{t('offers.new.shipping.stockLabel')}</label>
                    <span className="dash-svc-block-hint">{t('offers.new.shipping.stockByVariant')}</span>
                  </div>
                ) : (
                <div className="dash-svc-setting-block">
                  <label id="ofw-stock-label">{t('offers.new.shipping.stockLabel')}</label>
                  <span className="dash-svc-block-hint">{t('offers.new.shipping.stockHint')}</span>
                  <div className={`dash-svc-segmented${trackStock ? ' is-second' : ''}`} role="radiogroup" aria-labelledby="ofw-stock-label">
                    <button type="button" role="radio" aria-checked={!trackStock} className={!trackStock ? 'is-on' : ''} onClick={() => { setTrackStock(false); setInvalid(null) }}>
                      <IconInfinity size={14} /> {t('offers.new.shipping.unlimited')}
                    </button>
                    <button type="button" role="radio" aria-checked={trackStock} className={trackStock ? 'is-on' : ''} onClick={() => setTrackStock(true)}>
                      <IconPackage size={14} /> {t('offers.new.shipping.limited')}
                    </button>
                  </div>
                  {trackStock ? (
                    <label className={`dash-svc-field dash-ship-stock${invalid?.field === 'stock' ? ' is-invalid' : ''}`}>
                      <span>{t('offers.new.shipping.units')}</span>
                      <input
                        inputMode="numeric"
                        value={stock}
                        placeholder="10"
                        autoFocus
                        aria-invalid={invalid?.field === 'stock' || undefined}
                        onChange={(event) => { setStock(event.target.value.replace(/[^\d]/g, '')); if (invalid?.field === 'stock') setInvalid(null) }}
                      />
                      {invalid?.field === 'stock'
                        ? <small className="dash-svc-field-error">{invalid.message}</small>
                        : <small>{t('offers.new.shipping.unitsHint')}</small>}
                    </label>
                  ) : null}
                </div>
                )}

                <div className="dash-svc-setting-block">
                  <label>{t('offers.new.shipping.ratesLabel')}</label>
                  <span className="dash-svc-block-hint">{t('offers.new.shipping.ratesHint')}</span>
                  {shipping ? (
                    <ShippingRatesEditor
                      projectId={projectId}
                      token={token}
                      rates={shipping.rates}
                      maxRates={shipping.maxRates}
                      onChange={(rates) => { setShipping((current) => (current ? { ...current, rates } : current)); if (invalid?.field === 'rates') setInvalid(null) }}
                    />
                  ) : shippingFailed ? (
                    <small className="dash-svc-field-error">{t('shipping.loadFailed')}</small>
                  ) : (
                    <p className="dash-offer-card-foot"><IconLoader2 size={14} className="dash-spin" /> {t('shipping.loading')}</p>
                  )}
                  {invalid?.field === 'rates' ? <small className="dash-svc-field-error" role="alert">{invalid.message}</small> : null}
                </div>
              </section>
            ) : null}

            {/* ── 3. Qué pasa después ────────────────────────────────────── */}
            {step === 'delivery' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('offers.new.after.kicker')}</span>
                <h2>{t('offers.new.after.landing')}</h2>
                <p>{t(say('offers.new.after.landingLead'))}</p>

                <div className="dash-svc-setting-block">
                  <label>{t('offers.detail.thankYou.label')}</label>
                  <div className="dash-svc-choices">
                    {([
                      ['default', 'offers.detail.thankYou.default', 'offers.detail.thankYou.defaultFoot'],
                      ['page', 'offers.detail.thankYou.page', 'offers.new.after.pageHint'],
                      ['url', 'offers.detail.thankYou.url', 'offers.detail.thankYou.urlFoot'],
                    ] as const).map(([value, label, hint]) => (
                      <button
                        key={value}
                        type="button"
                        className={thankYouMode === value ? 'is-on' : ''}
                        onClick={() => { setThankYouMode(value); setInvalid(null) }}
                      >
                        <span className="dash-svc-choice-mark">{thankYouMode === value ? <IconCheck size={12} /> : null}</span>
                        <span className="dash-svc-choice-copy"><strong>{t(label)}</strong><small>{t(hint)}</small></span>
                      </button>
                    ))}
                  </div>

                  {thankYouMode === 'page' ? (
                    <label className={`dash-svc-field${invalid?.field === 'thankYouPage' ? ' is-invalid' : ''}`}>
                      <span>{t('offers.detail.thankYou.pageLabel')} <b>*</b></span>
                      <select
                        value={thankYouPageId}
                        disabled={pages === null}
                        onChange={(event) => { setThankYouPageId(event.target.value); setInvalid(null) }}
                      >
                        <option value="">{t(pages === null ? 'offers.detail.thankYou.loading' : 'offers.detail.thankYou.pick')}</option>
                        {(pages ?? []).map((page) => <option key={page.id} value={page.id}>{page.name}</option>)}
                      </select>
                      {invalid?.field === 'thankYouPage'
                        ? <small className="dash-svc-field-error">{invalid.message}</small>
                        : <small>{t('offers.detail.thankYou.pageFoot')}</small>}
                    </label>
                  ) : null}

                  {thankYouMode === 'url' ? (
                    <label className={`dash-svc-field${invalid?.field === 'thankYouUrl' ? ' is-invalid' : ''}`}>
                      <span>{t('offers.detail.thankYou.urlLabel')} <b>*</b></span>
                      <input
                        ref={urlRef}
                        type="url"
                        inputMode="url"
                        placeholder="https://…"
                        value={thankYouUrl}
                        onChange={(event) => { setThankYouUrl(event.target.value); setInvalid(null) }}
                      />
                      {invalid?.field === 'thankYouUrl'
                        ? <small className="dash-svc-field-error">{invalid.message}</small>
                        : <small>{t('offers.detail.thankYou.urlFoot')}</small>}
                    </label>
                  ) : null}
                </div>
              </section>
            ) : null}

            {/* ── Información adicional ──────────────────────────────────── */}
            {step === 'extra' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('offerFields.kicker')}</span>
                <h2>{t('offerFields.title')}</h2>
                <p>{t('offerFields.lead')}</p>
                <OfferCustomFieldsEditor
                  projectId={projectId}
                  token={token}
                  kind={offerTemplateKindOf({ type })}
                  values={customValues}
                  onChange={setCustomValues}
                />
              </section>
            ) : null}

            {/* ── 4. Revisión ────────────────────────────────────────────── */}
            {step === 'review' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('offers.new.review.kicker')}</span>
                <h2>{t('offers.new.review.title2')}</h2>
                <p>{t('offers.new.review.lead')}</p>

                {/* Cada línea con su «Editar»: el resumen no sirve de nada si
                    para arreglar una coma hay que buscar el paso en el raíl. */}
                <div className="dash-svc-review">
                  <div>
                    <span>{t('offers.new.review.title')}</span>
                    <strong>{title.trim() || <em>{t('offers.new.review.noTitle')}</em>}</strong>
                    <button type="button" onClick={() => goTo('info')}>{t('offers.new.review.edit')}</button>
                  </div>
                  <div>
                    <span>{t('offers.new.shortDescriptionLabel')}</span>
                    <strong className="dash-svc-review-excerpt">{shortDescription.trim() || <em>{t('offers.new.review.noDescription')}</em>}</strong>
                    <button type="button" onClick={() => goTo('info')}>{t('offers.new.review.edit')}</button>
                  </div>
                  <div>
                    <span>{t('offers.new.longDescriptionLabel')}</span>
                    <strong className="dash-svc-review-excerpt">{description.trim() || <em>{t('offers.new.review.noDescription')}</em>}</strong>
                    <button type="button" onClick={() => goTo('info')}>{t('offers.new.review.edit')}</button>
                  </div>
                  <div>
                    <span>{t('offers.new.review.billing')}</span>
                    <strong>{priceDecided ? billingLabel : <em>{t('offers.noPrice')}</em>}</strong>
                    <button type="button" onClick={() => goTo('price')}>{t('offers.new.review.edit')}</button>
                  </div>
                  {/* Aquí iba el tipo. No se cambia —lo eligió la puerta— y
                      repetirlo en el resumen era una línea que sólo se podía
                      leer: el resumen es para repasar decisiones, y ésta ya
                      está tomada y dicha en el título de la pantalla. */}
                  {/* Lo que se entrega, cuando hay algo que entregar. En el
                      alta no estaba porque el paso es el anterior y se acababa
                      de ver; editando se entra por cualquier lado. */}
                  {type === 'digital' ? (
                    <div>
                      <span>{t('offers.new.step.files')}</span>
                      <strong>{fileCount > 0 ? t(fileCount === 1 ? 'offers.edit.review.fileOne' : 'offers.edit.review.files', { count: fileCount }) : <em>{t('offers.edit.review.noFiles')}</em>}</strong>
                      <button type="button" onClick={() => goTo('files')}>{t('offers.new.review.edit')}</button>
                    </div>
                  ) : null}
                  {physical ? (
                    <div>
                      <span>{t('offers.new.step.variants')}</span>
                      <strong>
                        {hasVariants
                          ? variantsDraft.options.filter((option) => option.values.length).map((option) => `${option.name}: ${option.values.join(', ')}`).join(' · ')
                          : <em>{t('offers.new.variants.none')}</em>}
                      </strong>
                      <button type="button" onClick={() => goTo('variants')}>{t('offers.new.review.edit')}</button>
                    </div>
                  ) : null}
                  {physical ? (
                    <div>
                      <span>{t('offers.new.step.shipping')}</span>
                      <strong>
                        {hasVariants
                          ? t('offers.new.variants.stockByVariant')
                          : trackStock && stockValue !== null && !Number.isNaN(stockValue)
                          ? t('offers.new.review.stockCount', { count: stockValue })
                          : t('offers.new.shipping.unlimited')}
                        {' · '}
                        {activeRates.length
                          ? activeRates.map((rate) => `${rate.name} (${rateSummary(rate, currency, t)})`).join(', ')
                          : <em>{t('offers.new.review.noRates')}</em>}
                      </strong>
                      <button type="button" onClick={() => goTo('shipping')}>{t('offers.new.review.edit')}</button>
                    </div>
                  ) : null}
                  <div>
                    <span>{t('offers.new.review.after')}</span>
                    <strong>{afterLabel}</strong>
                    <button type="button" onClick={() => goTo('delivery')}>{t('offers.new.review.edit')}</button>
                  </div>
                </div>

                {/*
                 * Publicar o dejarla en borrador, aquí y no después.
                 *
                 * Era una frase que informaba —«se creará como borrador»— y no
                 * una decisión, así que quien ya tenía claro que la quería a la
                 * venta terminaba el asistente, buscaba la oferta en la lista y
                 * volvía a entrar para publicarla. Tres pasos para algo que ya
                 * sabía en el segundo.
                 *
                 * Borrador sigue siendo lo que viene puesto: mirar la oferta
                 * antes de enseñarla es lo prudente, y lo prudente es lo que
                 * debe pasar cuando alguien no decide nada.
                 */}
                <div className="dash-svc-setting-block dash-svc-publish-block">
                  <label id="ofw-publish-label">{t(editing ? 'offers.edit.onSave' : 'offers.new.review.onCreate')}</label>
                  <div
                    className={`dash-svc-segmented${publish ? ' is-second' : ''}`}
                    role="radiogroup"
                    aria-labelledby="ofw-publish-label"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!publish}
                      className={!publish ? 'is-on' : ''}
                      disabled={saving}
                      onClick={() => setPublish(false)}
                    ><IconPencil size={14} /> {t('offers.status.draft')}</button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={publish}
                      className={publish ? 'is-on' : ''}
                      disabled={saving}
                      onClick={() => {
                        if (!priceDecided) {
                          setStep('price')
                          return
                        }
                        /* El cupo antes que Stripe, como en `publishBlockFor`:
                           el orden decide qué ventana sale, y si aquí fuera al
                           revés la misma situación se explicaría de dos maneras
                           según se entre por el asistente o por la tabla. Y es
                           el orden correcto: con el cupo lleno no se publica
                           aunque la cuenta de cobros esté lista. */
                        if (publishBlockedByPlan) {
                          setLimitDialog(true)
                          return
                        }
                        if (blockedByStripe) {
                          setPublishBlockDialog(canConnectStripe ? 'stripe' : 'plan')
                          return
                        }
                        setPublish(true)
                      }}
                    ><IconWorld size={14} /> {t('offers.status.active')}</button>
                  </div>
                  {/* Sólo cuando hay algo que advertir: en borrador, que nadie
                      puede comprarla todavía, y sin precio, por qué no se deja
                      elegir lo otro. Publicándola no se dice nada — la frase que
                      había explicaba cómo funciona el botón de compra, que no es
                      lo que se está decidiendo aquí. */}
                  {!priceDecided || !publish ? (
                    <small>
                      {!priceDecided
                        ? t('offers.new.review.needsPrice')
                        : t('offers.new.review.draftHint')}
                    </small>
                  ) : null}
                </div>
              </section>
            ) : null}

            {/* Sólo lo que NO tiene campo al que señalar. Lo demás sale junto a
                su campo, que es donde hay que hacer algo. */}
            {error || stripe.error ? <p className="dash-svc-error" role="alert">{error || stripe.error}</p> : null}

            {/* Al pie del formulario y no en la cabecera: es donde está la
                mirada al terminar de rellenar el paso. */}
            <div className="dash-svc-step-actions">
              {stepIndex > 0
                ? <button type="button" className="dash-secondary-btn" disabled={saving} onClick={() => goTo(steps[stepIndex - 1]?.key ?? 'info')}><IconArrowLeft size={14} /> {t('offers.new.back')}</button>
                : <button type="button" className="dash-secondary-btn" disabled={saving} onClick={onClose}>{t('offers.new.cancel')}</button>}
              {stepIndex < steps.length - 1
                /* Editando, avanzar no es el remate de nada: guardar vive arriba
                   y desde cualquier paso, que es lo que hace falta para venir a
                   cambiar UNA cosa. Así el botón lleno no compite con él. */
                ? (
                  <button type="button" className={editing ? 'dash-secondary-btn' : 'dash-primary-btn'} onClick={next}>
                    {t('offers.new.nextStep')} <IconArrowRight size={14} />
                  </button>
                )
                : (
                  /* Y al final del recorrido, guardar también aquí: quien repasa
                     los pasos llega al resumen y ahí se acaba la pantalla, así
                     que subir a buscar el botón justo al terminar sobra. Es el
                     mismo `create`, no otro camino. */
                  <button type="button" className="dash-primary-btn" disabled={saving} onClick={() => { void create() }}>
                    {saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCheck size={14} />}
                    {t(saving
                      ? (editing ? 'offers.edit.saving' : 'offers.new.creating')
                      : editing ? 'offers.edit.save' : publish ? 'offers.new.createPublish' : 'offers.new.create')}
                  </button>
                )}
            </div>
          </div>
        </form>

        <aside className="dash-svc-wizard-preview" aria-label={t('offers.new.previewAria')}>
          {/*
            En el paso del destino, la maqueta se aparta.

            Ahí no se está decidiendo cómo se ve la página de pago sino a dónde
            va el comprador CUANDO YA PAGÓ, y eso no se comprueba en la página
            anterior. Se cambia por el destino, que es lo que se está eligiendo.
          */}
          {step === 'delivery' ? (
            <>
              <div className="dash-svc-preview-heading">
                <span>{t('offers.new.after.previewTitle')}</span>
                <em>{t('offers.new.after.previewHint')}</em>
              </div>
              <div className="dash-ofw-thanks" data-mode={thankYouMode}>
                <span className="dash-ofw-thanks-mark" aria-hidden="true"><IconCheck size={22} /></span>
                {thankYouMode === 'default' ? (
                  <>
                    <strong>{t('offers.new.thanks.title')}</strong>
                    <p>{t('offers.new.thanks.body')}</p>
                    <span className="dash-ofw-thanks-amount">
                      {mode === 'free' ? t('offers.new.freeTitle') : formattedPreview || t('offers.noPrice')}
                    </span>
                    <span className="dash-ofw-thanks-cta">{t('offers.new.thanks.back')}</span>
                  </>
                ) : thankYouMode === 'page' ? (
                  <>
                    <strong>{chosenPage?.name ?? t('offers.new.thanks.pagePending')}</strong>
                    <p>{t('offers.new.thanks.pageBody')}</p>
                  </>
                ) : (
                  <>
                    <strong className="dash-ofw-thanks-url"><IconExternalLink size={14} aria-hidden="true" /> {thankYouUrl.trim() || t('offers.new.thanks.urlPending')}</strong>
                    <p>{t('offers.new.thanks.urlBody')}</p>
                  </>
                )}
              </div>
              <p className="dash-svc-preview-note">{t('offers.new.after.previewNote')}</p>
            </>
          ) : (
            <>
              <div className="dash-svc-preview-heading">
                <span>{t('offers.new.preview')}</span>
                <em>{t('offers.new.previewHint')}</em>
              </div>
              {/* La barra del navegador dice `checkout.stripe.com` porque es
                  ahí donde ocurre: el cobro sale del sitio. Poner el dominio
                  del proyecto habría maquetado una página que no existe. */}
              <div className={`dash-svc-browser is-zoom-${zoom}`} data-focus={focusZone ?? undefined}>
                <div className="dash-svc-browser-bar"><i /><i /><i />
                  {/* Gratis no sale del sitio: no hay página de pago que
                      enseñar, así que la barra dice el sitio del proyecto. */}
                  <span className="dash-svc-browser-url">{mode === 'free' ? t('offers.new.checkout.ownSite') : CHECKOUT_HOST}</span>
                </div>
                {/* Dos capas: el marco que RECORTA y no se mueve, y la página
                    que se escala dentro. Sin el recorte, acercarse estiraba el
                    marco y se leía como una página más larga. */}
                <div className="dash-svc-page-viewport" ref={viewportRef}>
                  <div className="dash-svc-page-scale" ref={scaleRef}>
                    <div className="dash-svc-page">
                      <div className="dash-svc-page-info" data-zone="summary">
                        <span className="dash-svc-page-eyebrow">{t('offers.new.checkout.eyebrow')}</span>
                        {/* Fantasmas, no texto de relleno: «El título de tu
                            oferta» se lee como si la página YA dijera eso, y
                            más de uno cierra el asistente creyendo que ese
                            texto es el que se va a publicar. Una barra gris no
                            se puede confundir con contenido. */}
                        {title.trim()
                          ? <strong className="dash-svc-page-title" data-zone="title">{title.trim()}</strong>
                          : <span className="dash-svc-ghost is-title" data-zone="title" aria-hidden="true" />}
                        {shortDescription.trim()
                          ? <p className="dash-svc-page-desc" data-zone="desc">{shortDescription.trim()}</p>
                          : <span className="dash-svc-ghost-lines" data-zone="desc" aria-hidden="true"><i /><i /><i /></span>}
                        {priceDecided
                          ? (
                            <span className="dash-svc-page-price" data-zone="price">
                              {mode === 'free' ? t('offers.new.freeTitle') : formattedPreview}
                              {mode === 'subscription' ? <small>{t(CADENCE_SUFFIX_KEY[cadence])}</small> : null}
                            </span>
                          )
                          : <span className="dash-svc-ghost is-price" data-zone="price" aria-hidden="true" />}
                        {/* La portada, dentro de la columna y debajo del precio,
                            igual que en el asistente de reservas: en banda a
                            sangre pegada al techo pesaba más que el título, y lo
                            que se viene a mirar aquí es qué se vende y a cuánto.

                            La zona de la cámara va en la propia imagen, así que
                            se mide donde esté y no hace falta un marcador aparte
                            con el alto escrito a mano. */}
                        {image
                          ? <PreviewGallery images={images} />
                          : <span className="dash-svc-ghost is-image" data-zone="media" aria-hidden="true" />}
                      </div>

                      {/* La otra mitad: el formulario de pago. No se configura
                          —lo dibuja Stripe— pero sin él la maqueta no se parece
                          a la página que verá el comprador, que es su único
                          trabajo.

                          Gratis no tiene formulario porque no hay cobro: el
                          botón de la página entrega directamente. Dibujarle una
                          tarjeta de crédito al lado sería la peor forma de
                          explicar qué es gratis. */}
                      <div className="dash-svc-page-form">
                        {/* Gratis también tiene formulario: no el de la tarjeta,
                            el que recoge a quién le interesó. Es el trato de un
                            recurso gratuito y hay que poder verlo antes de
                            crearlo — dibujar aquí una entrega sin nada de por
                            medio prometería otra cosa. */}
                        {mode === 'free' ? (
                          <div className="dash-svc-page-fields">
                            <span className="dash-svc-page-field"><IconUser size={10} aria-hidden="true" />{t('offers.new.checkout.name')}</span>
                            <span className="dash-svc-page-field"><IconAt size={10} aria-hidden="true" />{t('offers.new.checkout.email')}</span>
                          </div>
                        ) : (
                          <div className="dash-svc-page-fields">
                            <span className="dash-svc-page-field"><IconAt size={10} aria-hidden="true" />{t('offers.new.checkout.email')}</span>
                            {/* Un producto físico: la dirección y la forma de
                                envío, como las pide Stripe, con cada tarifa ya
                                resuelta para este precio (gratis desde…). */}
                            {physical ? (
                              <div className="dash-ofw-page-shipping" data-zone="shipping">
                                <span className="dash-svc-page-field"><IconMapPin size={10} aria-hidden="true" />{t('offers.new.checkout.address')}</span>
                                {activeRates.length ? (
                                  <span className="dash-ofw-page-rates">
                                    {activeRates.map((rate, index) => {
                                      const subtotal = toMinorUnits(amount, currency) ?? 0
                                      const cost = rate.kind === 'pickup' || (rate.freeOver !== null && subtotal >= rate.freeOver) ? 0 : rate.amount
                                      return (
                                        <span key={rate.id} className={`dash-ofw-page-rate${index === 0 ? ' is-on' : ''}`}>
                                          <i aria-hidden="true" />
                                          <span>{rate.name}</span>
                                          <b>{cost > 0 ? formatMoney(cost, currency) : t('shipping.rate.free')}</b>
                                        </span>
                                      )
                                    })}
                                  </span>
                                ) : <span className="dash-svc-ghost-lines" aria-hidden="true"><i /><i /></span>}
                              </div>
                            ) : null}
                            <span className="dash-svc-page-field"><IconCreditCard size={10} aria-hidden="true" />{t('offers.new.checkout.card')}</span>
                            <span className="dash-svc-page-field"><IconUser size={10} aria-hidden="true" />{t('offers.new.checkout.name')}</span>
                          </div>
                        )}
                        <b className="dash-svc-page-cta">
                          {t(mode === 'free'
                            ? 'offers.new.checkout.get'
                            : mode === 'subscription' ? 'offers.new.checkout.subscribe' : 'offers.new.checkout.pay')}
                        </b>
                        {mode !== 'free'
                          ? <span className="dash-ofw-page-foot"><StripeMark size={11} /> {t('offers.new.checkout.foot')}</span>
                          : <span className="dash-ofw-page-foot">{t('offers.new.checkout.freeNote')}</span>}
                      </div>
                      {description.trim() ? <section className="dash-svc-page-details" data-zone="details">
                        <b>{t('offers.new.longDescriptionLabel')}</b><p>{description.trim()}</p>
                      </section> : null}
                    </div>
                  </div>
                </div>
              </div>
              <p className="dash-svc-preview-note">
                {step === 'review' ? t('offers.new.preview.noteFull') : t('offers.new.preview.noteZoom')}
              </p>
            </>
          )}
        </aside>
      </div>

      {/* La misma ventana de espera que Configuración → Stripe, no una copia: el
          alta ocurre fuera y lo que hay que decir mientras tanto es idéntico. */}
      {stripe.awaiting ? <StripeOnboardingDialog status={stripe.status} onClose={stripe.dismiss} /> : null}


      {/* La oferta del plan, en su ventana: es la misma que sale al toparse con
          el tope de páginas o de colecciones, y verla igual en los tres sitios
          es lo que la hace reconocible sin leerla. */}
      {limitDialog ? (
        <PlanLimitModal
          resource={t('usage.stat.offers').toLowerCase()}
          kind="offers"
          currentPlan={plan}
          onClose={() => setLimitDialog(false)}
          onOpenPlans={() => { setLimitDialog(false); handleOpenPlans() }}
        />
      ) : null}

      {publishBlockDialog ? (
        <PublishBlockDialog
          kind={publishBlockDialog}
          itemType={type === 'digital' ? 'digital' : 'service'}
          connecting={stripe.connecting}
          onConnect={() => { void stripe.connect() }}
          onOpenPlans={handleOpenPlans}
          onClose={() => setPublishBlockDialog(null)}
        />
      ) : null}
    </div>,
    document.body,
  )
}

/**
 * El formato, en dos letras y para leer.
 *
 * Del tipo declarado y no de la extensión del nombre: el nombre se puede
 * cambiar aquí mismo, y con él cambiaría un dato que describe el contenido.
 */
function fileKind(contentType: string, name: string): string {
  const sub = (contentType || '').split('/')[1] ?? ''
  const clean = sub.split(';')[0]?.replace(/^x-/, '').replace(/^vnd\..*[.-]/, '') ?? ''
  if (clean && clean.length <= 12) return clean.toUpperCase()
  const ext = name.includes('.') ? name.split('.').pop() ?? '' : ''
  return ext ? ext.toUpperCase() : ''
}

/**
 * Un entregable, con su dibujo, su ficha técnica y su nombre editable.
 *
 * Lo mismo para los que ya están subidos y para los que se acaban de elegir: la
 * única diferencia es la marca de «sin subir todavía», porque para quien mira
 * son la misma cosa —lo que se entrega al comprar— y separarlos en dos formas
 * obligaría a sumar de cabeza cuántos van a quedar.
 */
function FileCard({ name, size, contentType, pendingLabel, renaming, onRenameStart, onRenameChange, onRenameCancel, onRenameCommit, onRemove }: {
  name: string
  size: number
  contentType: string
  /** «sin subir todavía», o `null` si ya está en el servidor. */
  pendingLabel: string | null
  /** El texto que se está escribiendo, o `null` si no se está renombrando. */
  renaming: string | null
  onRenameStart: () => void
  onRenameChange: (value: string) => void
  onRenameCancel: () => void
  onRenameCommit: (value: string) => void
  onRemove: () => void
}) {
  const { t } = useI18n()
  const kind = fileKind(contentType, name)
  return (
    <li className={`dash-ofw-file${renaming !== null ? ' is-renaming' : ''}`}>
      <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-ofw-file-icon is-fog" aria-hidden="true">
        {createElement(fileIconFor(contentType), { size: 17 })}
      </span>

      <span className="dash-ofw-file-body">
        {renaming !== null ? (
          /* El nombre se edita donde se lee, sin cuadro aparte: es un campo que
             se abre en el sitio del texto y se cierra con Enter o con Escape,
             como el título de una ficha. */
          <input
            autoFocus
            className="dash-ofw-file-rename"
            value={renaming}
            maxLength={200}
            aria-label={t('offers.new.file.renameAria')}
            onChange={(event) => onRenameChange(event.target.value)}
            onBlur={(event) => onRenameCommit(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); onRenameCommit(event.currentTarget.value) }
              if (event.key === 'Escape') { event.preventDefault(); onRenameCancel() }
            }}
          />
        ) : (
          <strong title={name}>{name}</strong>
        )}
        {/* La ficha técnica en una línea de puntos: formato, peso y estado. El
            estado sólo cuando dice algo —«ya está subido» no es noticia—. */}
        <small>
          {[kind, fileSizeLabel(size), pendingLabel].filter(Boolean).join(' · ')}
        </small>
      </span>

      <span className="dash-ofw-file-actions">
        <button
          type="button"
          className="dash-icon-btn"
          aria-label={t('offers.new.file.rename')}
          data-tooltip={t('offers.new.file.rename')}
          onClick={onRenameStart}
        >
          <IconPencil size={14} />
        </button>
        <button
          type="button"
          className="dash-icon-btn"
          aria-label={t('offers.detail.files.removeAria', { name })}
          data-tooltip={t('offers.detail.files.remove')}
          onClick={onRemove}
        >
          <IconTrash size={14} />
        </button>
      </span>
    </li>
  )
}
